/**
 * PostHog analytics wrapper. This is the ONLY module in the app that imports
 * `posthog-js` / `@posthog/react` directly — everything else goes through the
 * exports here. That keeps the SDK swappable and gives us one place to stay
 * SSR-safe and to type our event names.
 *
 * `<PostHogProvider apiKey={POSTHOG_KEY} options={posthogOptions(...)}>` (mounted in
 * `__root.tsx`) initializes PostHog by calling `posthog.init()` on the global
 * `posthog-js` singleton. The wrapper functions below delegate to that same
 * singleton, so they work from components OR plain modules; in components,
 * `usePostHog()` returns the same instance.
 */
import posthog, { type CaptureResult } from 'posthog-js'

import { CONSENT_VERSION, type ConsentChoice } from './consent'

/** Public, client-safe project key. Empty → provider skips init, wrapper no-ops. */
export const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as
  | string
  | undefined

/**
 * Build SHA, baked into the client bundle at build time (VITE_SERVICE_VERSION
 * build-arg = the image tag). Unlike the deployment env, the version is fixed per
 * build, so build-time is correct — the same promoted image carries the same
 * version in staging and prod. Stamped on every event so any capture is
 * attributable to the exact release, matching the backend's `service.version`.
 */
export const SERVICE_VERSION = import.meta.env.VITE_SERVICE_VERSION as
  | string
  | undefined

declare global {
  interface Window {
    /** Deployment env injected by the server at request time — see __root.tsx. */
    __DEPLOYMENT_ENV__?: string
  }
}

/**
 * Deployment environment (`staging` | `production` | …), stamped onto every
 * captured event so insights/funnels can be scoped or split per env — the
 * PostHog counterpart to the backend's `deployment.environment` OTel resource
 * attribute.
 *
 * Read at RUNTIME from `window.__DEPLOYMENT_ENV__`, which the server injects into
 * the page (see __root.tsx) from `process.env.DEPLOYMENT_ENV`. Deliberately NOT a
 * build-time `import.meta.env.VITE_*` value: CI builds ONE image and promotes the
 * SAME artifact staging→prod, so a baked value couldn't tell the two apart. One
 * PostHog project; the `environment` property is the split. Empty in local dev /
 * during SSR → events carry no `environment` (and usually no key either, so the
 * wrapper no-ops anyway).
 */
function currentDeploymentEnv(): string | undefined {
  if (typeof window === 'undefined') return undefined
  const value = window.__DEPLOYMENT_ENV__
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * Options passed to `PostHogProvider`. `api_host: '/ingest'` routes all
 * ingestion through our own first-party reverse proxy (`src/routes/ingest/$.ts`)
 * so ad-blockers don't drop it; `ui_host` keeps the toolbar/links pointed at
 * PostHog. `capture_exceptions` powers error tracking; session-replay masks all
 * inputs. `person_profiles: 'identified_only'` keeps anonymous marketing traffic
 * as cheap anonymous events (no person profile until a user logs in) — web
 * analytics and funnels still work, at a fraction of the cost.
 *
 * A function rather than a constant because `optOutByDefault` is per-visitor:
 * it comes from the consent mode the server resolved for this request plus any
 * choice the visitor has already stored. See `lib/analytics/consent.ts`. The
 * result must be computed ONCE per mount (`useMemo` in __root.tsx) — a new
 * object on every render re-runs `posthog.init()`.
 *
 * `opt_out_capturing_by_default` applies only when posthog-js has no persisted
 * choice of its own. Once the visitor clicks Accept or Decline, posthog-js
 * records that and it wins on every later visit.
 */
export function posthogOptions(optOutByDefault: boolean) {
  return {
    opt_out_capturing_by_default: optOutByDefault,
    // Version the key posthog-js stores its own opt-in/out record under, so
    // bumping CONSENT_VERSION invalidates BOTH records at once. Without this, a
    // visitor who accepted under an older policy keeps being captured while the
    // re-prompt is on screen: a persisted opt-in outranks
    // opt_out_capturing_by_default.
    consent_persistence_name: `__ph_consent_v${CONSENT_VERSION}`,
    // Loads PostHog "site destinations" — client-side integrations configured in
    // the PostHog UI rather than in this repo. Required by the Reddit Pixel
    // destination, which is what plants Reddit's `_rdt_uuid` cookie (a match key
    // the Conversions API can then send) and feeds retargeting audiences.
    //
    // Gated on consent rather than hardcoded true, and this is the load-bearing
    // part: site apps are fetched at INIT, before any capture call, so
    // `opt_out_capturing_by_default` does not hold them back — and the Reddit
    // pixel sets THIRD-PARTY cookies, which is precisely what a consent notice
    // exists to gate. Passing the same flag we use for capturing means a visitor
    // who declined, or an EEA/UK visitor who has not chosen, never loads it. A
    // visitor who accepts picks it up on their next page load, since options are
    // computed once per mount.
    opt_in_site_apps: !optOutByDefault,
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',
    defaults: '2026-06-25',
    capture_exceptions: true,
    // Set explicitly rather than relying on the project's remote config: the
    // remote config reports web_vitals: true, but the SDK never loaded the
    // web-vitals extension, so no $web_vitals events were captured. Client
    // config wins, so this is the reliable switch. network_timing mirrors the
    // remote value — the object replaces it wholesale rather than merging.
    capture_performance: { web_vitals: true, network_timing: true },
    person_profiles: 'identified_only',
    session_recording: {
      maskAllInputs: true,
    },
    // Stamp the deployment environment onto EVERY event. Done here rather than
    // via `register()` because `resetAnalytics()` (logout) calls `posthog.reset()`,
    // which clears registered super properties — a `before_send` hook survives
    // reset and can't be dropped, so staging and prod events stay distinguishable
    // regardless of session state. `environment` is a bounded, low-cardinality
    // value, so it's safe as a breakdown/filter dimension.
    before_send: (event: CaptureResult | null): CaptureResult | null => {
      if (event) {
        const environment = currentDeploymentEnv()
        if (environment) {
          event.properties = { ...event.properties, environment }
        }
        if (SERVICE_VERSION) {
          event.properties = {
            ...event.properties,
            service_version: SERVICE_VERSION,
          }
        }
      }
      return event
    },
  } as const
}

/**
 * The product events we've standardized on. The `(string & {})` member keeps
 * the union open — callers can pass any snake_case name and still get
 * autocomplete for these. Grouped by funnel: activation, monetization, growth,
 * churn.
 *
 * Shared with the SERVER-side capture wrapper (`posthog-server.ts`), so one
 * vocabulary covers both. Which side owns an event matters, because the ad
 * destinations read the authoritative ones:
 *
 *  - `signup` / `purchase` are server-side and authoritative. They fire once,
 *    from the account row's creation hook and from the shared grant/credit
 *    functions, and cannot be blocked, forged or replayed by a browser.
 *  - `signup_submitted` / `checkout_returned` are the client's view of the same
 *    moments: an attempt, and a post-checkout redirect. Useful for funnel
 *    drop-off, useless as conversions — the redirect can be skipped, refreshed
 *    or hand-typed. Never send these to an ad platform.
 */
export type AnalyticsEvent =
  // activation
  /** Server-side, from the Better Auth `user.create.after` hook. */
  | 'signup'
  /** Client-side signup ATTEMPT — see the note above. */
  | 'signup_submitted'
  | 'login'
  | 'logout'
  | 'org_created'
  | 'credential_created'
  | 'connection_string_copied'
  // monetization
  /** Server-side, from the shared grant/credit functions — every payment rail. */
  | 'purchase'
  | 'subscription_checkout_started'
  /** Client-side post-checkout redirect — see the note above. */
  | 'checkout_returned'
  | 'trial_checkout_started'
  | 'trial_cancelled'
  /** Server-side, from the trial grant — see attribution-server.ts. */
  | 'trial_started'
  | 'data_topup_started'
  | 'data_topup_submitted'
  | 'billing_portal_opened'
  // support
  | 'support_request_submitted'
  // growth / churn
  | 'org_member_invited'
  | 'credential_deleted'
  | 'org_deleted'
  | (string & {})

/**
 * True only in the browser with a configured key — mirrors when the provider
 * actually initializes PostHog, so the wrapper stays a clean no-op during SSR
 * and in local dev without a key (no console noise, no buffered events).
 */
function enabled(): boolean {
  return typeof window !== 'undefined' && !!POSTHOG_KEY
}

/** Tracks a product event. No-ops during SSR or when no key is configured. */
export function capture(
  event: AnalyticsEvent,
  props?: Record<string, unknown>,
): void {
  if (enabled()) posthog.capture(event, props)
}

/** Associates the current session with a known user. Call on login/app load. */
export function identify(
  userId: string,
  props?: Record<string, unknown>,
): void {
  if (enabled()) posthog.identify(userId, props)
}

/**
 * Hands the Support widget a server-signed identity so tickets follow the
 * account across browsers. Call AFTER `identify()` with the same id:
 * `setIdentity()` also calls `alias(distinctId)`, which is a no-op only when
 * that id is already the current distinct id.
 */
export function setSupportIdentity(distinctId: string, hash: string): void {
  if (enabled()) posthog.setIdentity(distinctId, hash)
}

/**
 * Clears identity + registered super properties. Call this on logout.
 *
 * A no-op unless PostHog still holds an identified user. The caller runs on
 * every signed-out page load, and resetting an anonymous visitor there would
 * mint a new distinct id per page load and tear down the Support widget's
 * conversations state (`reset()` clears its stored session and tickets).
 */
export function resetAnalytics(): void {
  if (!enabled() || !posthog._isIdentified()) return
  // `reset()` does not drop the Support identity, so clear it explicitly or
  // the next person on this browser would see the previous user's tickets.
  posthog.clearIdentity()
  posthog.reset()
}

/**
 * Applies a consent choice to the live posthog-js instance and persists it in
 * posthog's OWN storage, so the choice survives independently of our
 * localStorage record (see `lib/analytics/consent.ts`).
 *
 * Accepting after an `opt-in` default flushes what init suppressed — posthog-js
 * captures the pending pageview and starts session replay at that point.
 * Declining stops capture and clears the distinct-id cookie.
 */
export function applyConsent(choice: ConsentChoice): void {
  if (!enabled()) return
  if (choice === 'denied') {
    posthog.opt_out_capturing()
    return
  }
  // Already capturing — either an `opt-out` region default, or an Accept from
  // an earlier visit that posthog-js persisted. Calling opt_in again would
  // emit a second `$opt_in` event for the same decision.
  if (!posthog.has_opted_out_capturing()) return
  posthog.opt_in_capturing()
}

/**
 * Registers super properties that ride along on EVERY subsequent event, so any
 * insight/funnel can break down or filter by them. We use this for org context
 * (`org_id`, `org_plan`, …) — the free substitute for paid Group Analytics.
 * Re-register on org switch; `resetAnalytics()` clears them on logout.
 */
export function register(props: Record<string, unknown>): void {
  if (enabled()) posthog.register(props)
}

/** Manually reports a caught error/exception to PostHog error tracking. */
export function captureException(
  error: unknown,
  props?: Record<string, unknown>,
): void {
  if (enabled()) posthog.captureException(error, props)
}

// Provider + hooks from the React SDK. App code imports these from here, never
// from `@posthog/react` directly. Use the `useFeatureFlag*` hooks for flags —
// they handle loading state and live flag updates.
export {
  PostHogProvider,
  useFeatureFlagEnabled,
  useFeatureFlagPayload,
  usePostHog,
} from '@posthog/react'
